import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ClaimCard = ({ claim }) => {
  const navigate = useNavigate();

  const getVerdictColor = (verdict) => {
    const colors = {
      'true': 'text-success bg-success/10 border-success/20',
      'mostly-true': 'text-success bg-success/10 border-success/20',
      'mixed': 'text-warning bg-warning/10 border-warning/20',
      'mostly-false': 'text-destructive bg-destructive/10 border-destructive/20',
      'false': 'text-destructive bg-destructive/10 border-destructive/20',
      'unverified': 'text-muted-foreground bg-muted border-border'
    };
    return colors?.[verdict] || colors?.unverified;
  };

  const getVerdictIcon = (verdict) => {
    const icons = {
      'true': 'CheckCircle',
      'mostly-true': 'CheckCircle',
      'mixed': 'AlertCircle',
      'mostly-false': 'XCircle',
      'false': 'XCircle',
      'unverified': 'HelpCircle'
    };
    return icons?.[verdict] || icons?.unverified;
  };

  const handleCardClick = () => {
    navigate('/claim-detail-analysis', { state: { claimId: claim?.id } });
  };

  const handleShare = (e) => {
    e?.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: claim?.title,
        text: claim?.summary,
        url: window.location?.origin + `/claim/${claim?.id}`
      });
    } else {
      navigator.clipboard?.writeText(window.location?.origin + `/claim/${claim?.id}`);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-card border border-border rounded-lg p-6 hover:shadow-elevated transition-all duration-200 cursor-pointer group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {claim?.title}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {claim?.category} • {claim?.publishedDate}
          </p>
        </div>
        
        {/* Verdict Badge */}
        <div className={`
          flex items-center space-x-2 px-3 py-1.5 rounded-full border text-sm font-medium
          ${getVerdictColor(claim?.verdict)}
        `}>
          <Icon name={getVerdictIcon(claim?.verdict)} size={16} />
          <span className="capitalize">{claim?.verdict?.replace('-', ' ')}</span>
        </div>
      </div>
      {/* Summary */}
      <p className="text-foreground mb-4 line-clamp-3">
        {claim?.summary}
      </p>
      {/* Source Preview */}
      {claim?.sourcePreview && (
        <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg mb-4">
          <Image
            src={claim?.sourcePreview?.favicon}
            alt={claim?.sourcePreview?.domain}
            className="w-6 h-6 rounded"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {claim?.sourcePreview?.title}
            </p>
            <p className="text-xs text-muted-foreground">
              {claim?.sourcePreview?.domain}
            </p>
          </div>
        </div>
      )}
      {/* Confidence Score */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Confidence:</span>
          <div className="flex items-center space-x-1">
            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  claim?.confidenceScore >= 80 ? 'bg-success' :
                  claim?.confidenceScore >= 60 ? 'bg-warning' : 'bg-destructive'
                }`}
                style={{ width: `${claim?.confidenceScore}%` }}
              />
            </div>
            <span className="text-sm font-medium text-foreground">
              {claim?.confidenceScore}%
            </span>
          </div>
        </div>

        {/* EFCSN Badge */}
        {claim?.efcsnVerified && (
          <div className="flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full">
            <Icon name="Shield" size={14} />
            <span className="text-xs font-medium">EFCSN</span>
          </div>
        )}
      </div>
      {/* Provenance & Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Provenance Icons */}
          <div className="flex items-center space-x-1">
            {claim?.hasEvidence && (
              <Icon name="FileText" size={16} className="text-muted-foreground" />
            )}
            {claim?.hasC2PA && (
              <Icon name="Shield" size={16} className="text-primary" />
            )}
            {claim?.crossReferenced && (
              <Icon name="Link" size={16} className="text-muted-foreground" />
            )}
          </div>

          <span className="text-xs text-muted-foreground">
            Reviewed by {claim?.reviewerCount} experts
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            iconName="Share"
            onClick={handleShare}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
          <Button
            variant="ghost"
            size="icon"
            iconName="ExternalLink"
            onClick={(e) => {
              e?.stopPropagation();
              navigate('/claim-detail-analysis', { state: { claimId: claim?.id } });
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
      </div>
    </div>
  );
};

export default ClaimCard;